import { useTranslation } from 'react-i18next';

import { ReactComponent as CheckIcon } from '../../../../../../../../assets/icons/check.svg';
import CheckItem from '../../../../../../../../shared/components/Globals/CheckItem/CheckItem';
import type { IDataCheck, IDataCheckGroup } from '../../../../../../../../shared/interfaces/checks';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../../../shared/translations/prospects';
import './ActivationChecklist.scss';

interface IActivationChecklistProps {
  groups: IDataCheckGroup[];
  onCheckClick: (check: IDataCheck) => void;
}

const ActivationChecklist = ({ groups, onCheckClick }: IActivationChecklistProps) => {
  const { t } = useTranslation();
  const { checklistTitle, checklistCompleted, checklistItemsLeft } = TRANSLATIONS_PROSPECTS.detail.pendingActivation;

  const pendingCount = groups.reduce((total, group) => total + group.checks.filter((check) => !check.done).length, 0);

  return (
    <section className="activation-checklist" aria-live="polite">
      <h2 className="activation-checklist__title">{t(checklistTitle)}</h2>

      {groups.map((group) => {
        if (group.checks.length === 0) return null;

        const pending = group.checks.filter((check) => !check.done);
        const doneCount = group.checks.length - pending.length;

        return (
          <div key={group.id} className="activation-checklist__group">
            <span className="activation-checklist__group-name">
              {group.title ?? (group.titleKey ? t(group.titleKey, group.titleParams ?? {}) : '')}
            </span>

            <div className="activation-checklist__items">
              {pending.map((check) => (
                <CheckItem
                  key={check.id}
                  done={false}
                  required
                  label={t(check.labelKey, check.labelParams ?? {})}
                  onClick={() => onCheckClick(check)}
                />
              ))}

              {doneCount > 0 && (
                <div className="activation-checklist__done-summary">
                  <span className="activation-checklist__done-marker" aria-hidden="true">
                    <CheckIcon width={8} height={8} />
                  </span>
                  {t(checklistCompleted, { count: doneCount })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {pendingCount > 0 && <span className="sr-only">{t(checklistItemsLeft, { count: pendingCount })}</span>}
    </section>
  );
};

export default ActivationChecklist;
